import { EnvironmentInjector, Injectable, inject, runInInjectionContext } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  QueryFn
} from '@angular/fire/compat/firestore';

/**
 * Una colección de AngularFire **sin `doc()`**: `doc()` construye otro
 * `AngularFirestoreDocument`, y fuera de un contexto de inyección eso lanza `NG0203`. Un
 * documento se pide con `FirestoreRefs.doc()`.
 */
export type CollectionRef<T> = Omit<AngularFirestoreCollection<T>, 'doc'>;

/**
 * Un documento de AngularFire **sin `collection()`**, por lo mismo: una subcolección se pide
 * con `FirestoreRefs.collection()` y su ruta (`messages/<id>/chats`).
 */
export type DocumentRef<T> = Omit<AngularFirestoreDocument<T>, 'collection'>;

/**
 * El único punto de la app que construye referencias de `AngularFirestore`.
 *
 * **Por qué existe.** En AngularFire 20 `AngularFirestoreCollection` y
 * `AngularFirestoreDocument` hacen `inject(EnvironmentInjector)` en un **inicializador de
 * campo**, así que construirlas fuera de un contexto de inyección lanza `NG0203`. Contexto
 * de inyección es el constructor y poco más: un `ngOnInit`, un click, un callback o
 * cualquier cosa después de un `await` no lo son. `this.adb.collection(...)` desde ahí
 * rompía la lista de pedidos (la pantalla principal del comercio), el chat, el tracker y
 * todos los métodos de `ApisService` que leían o escribían por `AngularFirestore`.
 *
 * Acá la referencia se construye dentro de `runInInjectionContext(this.injector, ...)`, que
 * es lo que hace `FirestoreRepository` de `mitter-client-app`. Lo que devuelve es el mismo
 * objeto de AngularFire de siempre (`get()`, `snapshotChanges()`, `set()`, `update()`...),
 * y la consulta y la ruta son las mismas: no cambia qué se lee ni qué se escribe. Los
 * métodos de esos objetos no construyen nada, así que se pueden llamar fuera de contexto
 * sin problema; los dos que sí construyen (`doc()` de una colección y `collection()` de un
 * documento) están quitados del tipo, y el compilador no deja encadenarlos.
 *
 * **No inyectes `AngularFirestore` en otro lado**: el lint lo prohíbe
 * (`no-restricted-imports`) fuera de este archivo y de las pruebas. `firebase.firestore()`
 * directo (el `this.db` de `ApisService`) no tiene el problema.
 *
 * `mitter-driver-app` tiene una **copia** con el mismo nombre: si se corrige algo acá,
 * llevarlo allá.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreRefs {
  private adb = inject(AngularFirestore);
  private injector = inject(EnvironmentInjector);

  /**
   * Una colección, o una subcolección por su ruta (`foods/<uid>/all`), con su consulta.
   * Es `AngularFirestore.collection(path, queryFn)`, construida en contexto.
   */
  collection<T>(path: string, queryFn?: QueryFn): CollectionRef<T> {
    return runInInjectionContext(this.injector, () => this.adb.collection<T>(path, queryFn));
  }

  /**
   * Un documento por su colección (o ruta de subcolección) y su id. Es
   * `AngularFirestore.collection(path).doc(id)` —la misma forma que tenía la app—,
   * construida en contexto.
   */
  doc<T>(collectionPath: string, id: string): DocumentRef<T> {
    return runInInjectionContext(this.injector, () => this.adb.collection<T>(collectionPath).doc<T>(id));
  }
}
